import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../design-system/Icon";
import { Button } from "../../design-system/Button";
import { useAppStore } from "../../state/app-store";

/** Un atajo declarado: combinación + descripción + acción. */
interface Shortcut { readonly keys: string; readonly label: string }

const SHORTCUTS: readonly Shortcut[] = [
  { keys: "Ctrl + K", label: "Buscar" },
  { keys: "Ctrl + B", label: "Mostrar/ocultar el menú lateral" },
  { keys: "Ctrl + 1 … 9", label: "Ir a la sección N del menú" },
  { keys: "?", label: "Mostrar esta ayuda de atajos" },
  { keys: "Esc", label: "Cerrar diálogos y menús" },
];

const NAV_PATHS = ["/", "/producto", "/marketplace", "/ventas", "/precios", "/clientes", "/inventario", "/compras", "/proveedores"];

/** ¿El foco está en un campo de texto? (para no capturar atajos mientras se escribe). */
function inEditable(el: EventTarget | null): boolean {
  const t = el as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

/**
 * Atajos de teclado globales + diálogo de ayuda (item 12, accesibilidad). Se monta una vez en el
 * AppShell. No captura teclas mientras se escribe en un campo (salvo Escape).
 */
export function KeyboardShortcuts(): JSX.Element {
  const navigate = useNavigate();
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const editing = inEditable(e.target);
      if (e.key === "Escape") { setHelpOpen(false); return; }
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "k") { e.preventDefault(); document.getElementById("global-search")?.focus(); return; }
      if (mod && e.key.toLowerCase() === "b") { e.preventDefault(); toggleSidebar(); return; }
      if (mod && /^[1-9]$/.test(e.key)) {
        const path = NAV_PATHS[Number(e.key) - 1];
        if (path) { e.preventDefault(); navigate(path); }
        return;
      }
      if (!editing && (e.key === "?" || (e.shiftKey && e.key === "/"))) { e.preventDefault(); setHelpOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, toggleSidebar]);

  if (!helpOpen) return <></>;
  return (
    <div role="presentation" onClick={() => setHelpOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: "var(--sp-4)" }}>
      <div role="dialog" aria-modal="true" aria-label="Atajos de teclado" onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 14px)", maxWidth: 460, width: "100%", padding: "var(--sp-5)", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <div className="row" style={{ gap: 10, marginBottom: "var(--sp-4)" }}>
          <Icon name="bolt" size={20} style={{ color: "var(--brand)" }} />
          <h2 className="strong" style={{ fontSize: 16 }}>Atajos de teclado</h2>
        </div>
        <table className="table" style={{ width: "100%" }}>
          <tbody>
            {SHORTCUTS.map((s) => (
              <tr key={s.keys}>
                <td style={{ width: 130 }}><kbd style={{ fontSize: 12.5, background: "var(--bg-muted, rgba(127,127,127,0.15))", padding: "2px 8px", borderRadius: 6, border: "1px solid var(--border)" }}>{s.keys}</kbd></td>
                <td className="muted" style={{ fontSize: 13.5 }}>{s.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--sp-4)" }}>
          <Button size="sm" onClick={() => setHelpOpen(false)}>Entendido</Button>
        </div>
      </div>
    </div>
  );
}
